import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import i18n from '../i18n';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null, showDetails: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    console.error('[Mieteora ErrorBoundary caught error]:', error, errorInfo);

    // If chunk loading failed (e.g. after a new build was deployed), reload the page once
    const isChunkLoadFailed = error?.name === 'ChunkLoadError' ||
      /Loading chunk [\d]+ failed/i.test(error?.message || '') ||
      /Failed to fetch dynamically imported module/i.test(error?.message || '');

    if (isChunkLoadFailed) {
      const reloadKey = 'mieteora_chunk_reload_' + (error?.message || '');
      const hasReloaded = sessionStorage.getItem(reloadKey);
      if (!hasReloaded) {
        sessionStorage.setItem(reloadKey, 'true');
        window.location.reload();
      }
    }
  }

  toggleDetails = () => {
    this.setState(prev => ({ showDetails: !prev.showDetails }));
  };

  copyDetails = async () => {
    const details = [
      this.state.error?.toString() || '',
      this.state.errorInfo?.componentStack || ''
    ].filter(Boolean).join('\n\n');
    try {
      await navigator.clipboard.writeText(details);
    } catch (e) {
      console.warn('Clipboard copy failed', e);
    }
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center p-6 font-sans">
          <div className="max-w-md w-full bg-slate-900/90 border border-slate-800 rounded-2xl p-6 shadow-2xl backdrop-blur-xl space-y-6">
            <div className="flex items-center space-x-3 text-rose-500">
              <div className="p-3 bg-rose-500/10 rounded-xl">
                <AlertTriangle className="w-8 h-8 text-rose-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold tracking-tight text-white">{i18n.t('sweep.errorBoundary.title')}</h2>
                <p className="text-xs text-slate-400">{i18n.t('sweep.errorBoundary.subtitle')}</p>
              </div>
            </div>

            <p className="text-sm text-slate-300 leading-relaxed">
              {i18n.t('sweep.errorBoundary.description')}
            </p>

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={this.toggleDetails}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-medium transition-colors border border-slate-700/60"
              >
                {this.state.showDetails
                  ? i18n.t('sweep.errorBoundary.hideDetails', { defaultValue: 'Details ausblenden' })
                  : i18n.t('sweep.errorBoundary.showDetails', { defaultValue: 'Details anzeigen' })}
              </button>
              <button
                type="button"
                onClick={this.copyDetails}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-lg text-xs font-medium transition-colors border border-slate-700/60"
              >
                {i18n.t('sweep.errorBoundary.copyDetails', { defaultValue: 'Details kopieren' })}
              </button>
            </div>

            {this.state.showDetails && (this.state.error || this.state.errorInfo) && (
              <pre className="p-3 bg-slate-950 rounded-lg border border-slate-800 max-h-64 overflow-auto text-xs font-mono text-slate-300 whitespace-pre-wrap">
                {[
                  this.state.error?.toString() || '',
                  this.state.errorInfo?.componentStack || ''
                ].filter(Boolean).join('\n\n')}
              </pre>
            )}

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={this.handleReload}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-sky-600 hover:bg-sky-500 text-white rounded-xl text-sm font-medium transition-colors shadow-lg shadow-sky-600/20 active:scale-[0.98]"
              >
                <RefreshCw className="w-4 h-4" />
                {i18n.t('sweep.errorBoundary.reload')}
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-800 hover:bg-slate-700 text-slate-200 rounded-xl text-sm font-medium transition-colors border border-slate-700/60 active:scale-[0.98]"
              >
                <Home className="w-4 h-4" />
                {i18n.t('sweep.errorBoundary.dashboard')}
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
